import React from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { COMPANY_INFO, SITE_IMAGES, getCdnImage } from '../data/content';

export function ForProfessionelle() {
  return (
    <>
      <SEO
        title="For professionelle – Entreprenører, Tømrere, Murere & Arkitekter"
        description="SYSTEM 3E til professionelle byggeprojekter. Optimér fremdriften med op til 70%, undgå våde samlinger og reducér spild på pladsen. Direkte kontakt til den danske eneforhandler."
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.constructionSite, 1600, 80)}
            alt="Professionel montage på byggepladsen"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-[#162332]/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Løsninger til Professionelle
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              Maksimal effektivitet på byggepladsen
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
              Forkort råhusfasen, reducér mandetimer og eliminer våde arbejdsgange. AGT Nordic Group ApS er din professionelle rådgiver og direkte leverandør i Danmark.
            </p>

            <NavLink
              to="/kontakt"
              className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-base transition-all inline-flex items-center gap-2 shadow-lg"
            >
              <span>Bliv kontaktet om SYSTEM 3E</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Key Professional Metrics */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#122a4e] font-bold text-xs uppercase tracking-widest block mb-2">
              Byggepladslogistik & Økonomi
            </span>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Kompakte fordele for den udførende entreprenør
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-gauge-high" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Ekstrem Montagehastighed</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Under optimale forhold opføres op til 100 m² ydervægge på et enkelt døgn. Færre mandetimer pr. kvadratmeter væg.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-boxes-packing" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Simpel Logistik & Lager</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Elementerne leveres færdigt beregnet og klar til brug. Ingen blandeanlæg, vandsiloer eller store poser lim på byggepladsen.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-[#122a4e]/10 text-[#122a4e] rounded-2xl flex items-center justify-center text-xl mb-6">
                  <i className="fa-solid fa-shield-cat" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Ingen Vinterstoppasser</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Systemet samles 100% tørt. Du undgår ufrivillige driftsstop pga. frost, da der ikke findes våde mørtelsamlinger.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Documentation / Architect Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#122a4e] font-extrabold text-xs uppercase tracking-widest block mb-2">
                Til Arkitekter & Konstruktører
              </span>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-6">
                Fuld teknisk dokumentation & projekteringsstøtte
              </h2>
              <p className="text-slate-700 text-sm leading-relaxed mb-6">
                AGT Nordic Group ApS bistår projekterende rådgivere med tegninger, tekniske specifikationer og U-værdiberegninger.
              </p>

              <div className="space-y-3 mb-8">
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-900 text-sm">Brandmodstandsklasse</p>
                    <p className="text-slate-500 text-xs">Maksimal sikkerhed til tæt-lav og etagebyggeri</p>
                  </div>
                  <span className="bg-[#122a4e]/10 text-[#122a4e] font-extrabold text-sm px-3 py-1 rounded-lg border border-[#122a4e]/20">
                    REI 240 + M
                  </span>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-slate-900 text-sm">U-værdi for basiselement</p>
                    <p className="text-slate-500 text-xs">Integreret perlit isoleringseffekt</p>
                  </div>
                  <span className="bg-slate-100 text-slate-900 font-extrabold text-sm px-3 py-1 rounded-lg border border-slate-200">
                    0,198 W/m²K
                  </span>
                </div>
              </div>

              <NavLink
                to="/kontakt"
                className="inline-flex items-center gap-2 text-[#122a4e] font-bold text-sm hover:text-[#1d3b66] transition-colors"
              >
                <span>Relevante datablad eller projekteringshjælp</span>
                <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
              </NavLink>
            </div>

            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-slate-900">
              <img
                src={getCdnImage(SITE_IMAGES.modularL01, 1000, 85)}
                alt="Erhvervs- og boligbyggeri med SYSTEM 3E"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Professional Call To Action */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#446ba9] font-extrabold text-xs uppercase tracking-widest block mb-2">
            Eneforhandler i Danmark
          </span>
          <h2 className="text-3xl font-black text-white mb-4">
            B2B Samarbejde & Prisaftaler
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto mb-8">
            Har du et aktuelt byggeprojekt? Kontakt {COMPANY_INFO.name} for engrospriser, projektberegning og prøveelementer.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <NavLink
              to="/kontakt"
              className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-base transition-all shadow-lg"
            >
              Bliv kontaktet om SYSTEM 3E
            </NavLink>
            <a
              href={COMPANY_INFO.phoneRaw}
              className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-all border border-slate-700 whitespace-nowrap"
            >
              Ring direkte til erhverv: {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
